import { useCallback, useEffect, useState, useMemo } from 'react';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';
import { useWatchlist, useWatchlists } from '@/hooks/useWatchlist';
import useUser from '@/hooks/useUser';
import { doesCompanyExistInWatchlist } from '@/pages/Watchlist/utils';
import { useWatchlistMutations } from '@/pages/Watchlist/hooks/useWatchlistMutations';
import StarOutlineIcon from '@simplywallst/ui-core/icons/StarOutline';
import StarSolidIcon from '@simplywallst/ui-core/icons/StarSolid';
import { useSummary } from '@/hooks/useCompany';
import { cnLite } from '../utils';
import { useCompanyReportTranslation } from '@/pages/CompanyReport/hooks/useTranslation';
import type { Action } from '../types';

const LimitModal = loadable(
  () => import('@/pages/Watchlist/components/LimitModal')
);
const GuestRegistrationModal = loadable(
  () => import('@/components/GuestRegistrationModal')
);
const AddToWatchlistsModal = loadable(
  () => import('../components/AddToWatchlistsModal'),
  { resolveComponent: (c) => c.AddToWatchlistsModal }
);

const MODAL = {
  NONE: 'none',
  COMPANY_LIMIT: 'company-limit-modal',
  GUEST_REGISTRATION: 'guest-registration-modal',
  ADD_TO_WATCHLISTS: 'add-to-watchlists-modal',
} as const;

type ActiveModal = (typeof MODAL)[keyof typeof MODAL];

export const useWatchAction = (): Action => {
  const summary = useSummary();
  const t = useCompanyReportTranslation();
  const { data: user } = useUser();
  const { trackEvent } = useTracking();
  const [modal, setModal] = useState<ActiveModal | null>(null);
  const { data: watchlist, isFetched: isWatchlistFetched } = useWatchlist();
  const { data: watchlists, isFetched: isWatchlistsFetched } = useWatchlists();
  const { addCompany, addCompanyStatus, removeCompany, removeCompanyStatus } =
    useWatchlistMutations();

  const hasFetched = isWatchlistFetched && isWatchlistsFetched;
  const hasMultipleWatchlists =
    typeof watchlists !== 'undefined' && watchlists.length > 1;
  const isLoading =
    addCompanyStatus === 'pending' || removeCompanyStatus === 'pending';
  const companyId = summary.status === 'success' ? summary.companyId : '';
  const tickerSymbol = summary.status === 'success' ? summary.tickerSymbol : '';

  const isWatching = useMemo(() => {
    const watchlistKeys = watchlist?.items.map((n) => n.companyId);
    const watchlistsKeys = watchlists?.flatMap((w) =>
      w.items.map((n) => n.companyId)
    );
    return doesCompanyExistInWatchlist(
      companyId,
      hasMultipleWatchlists ? watchlistsKeys : watchlistKeys
    );
  }, [companyId, hasMultipleWatchlists, watchlist, watchlists]);

  const handleLoggedOutAddClick = useCallback(() => {
    setModal(MODAL.GUEST_REGISTRATION);
    trackEvent({
      type: 'track',
      action: 'click',
      modifier: 'addToWatchlists',
      subject: 'guestRegistrationModal',
    });
  }, [trackEvent]);

  const handleRemoveCompany = useCallback(() => {
    if (typeof watchlist === 'undefined') {
      return;
    }
    removeCompany({ companyId, watchlistId: watchlist?.id });
    trackEvent({
      type: 'track',
      subject: 'WatchlistDesktopToggle',
      modifier: 'remove',
      action: 'click',
    });
  }, [trackEvent, removeCompany, companyId, watchlist]);

  const handleAddCompany = useCallback(() => {
    if (typeof watchlist === 'undefined') {
      return;
    }
    addCompany({ id: companyId, watchlistId: watchlist?.id });
    trackEvent({
      type: 'track',
      subject: 'WatchlistDesktopToggle',
      modifier: 'add',
      action: 'click',
    });
  }, [trackEvent, addCompany, companyId, watchlist]);

  useEffect(() => {
    if (user.loggedIn && modal === MODAL.GUEST_REGISTRATION) {
      // The user must have logged in or registered via the modal
      // So we hide the modal
      setModal(MODAL.NONE);

      // If the user has multiple watchlists, show the 'add to watchlists' modal
      // Otherwise, add the company to their watchlist
      if (hasMultipleWatchlists) {
        setModal(MODAL.ADD_TO_WATCHLISTS);
      } else if (watchlist) {
        addCompany({ id: companyId, watchlistId: watchlist?.id });
      }
    }
  }, [
    user.loggedIn,
    modal,
    addCompany,
    companyId,
    watchlist,
    hasMultipleWatchlists,
  ]);

  const handleClick = useCallback(() => {
    if (!user.loggedIn) {
      handleLoggedOutAddClick();
    } else if (hasMultipleWatchlists) {
      setModal('add-to-watchlists-modal');
    } else if (isWatching) {
      handleRemoveCompany();
    } else if (watchlist && watchlist.items.length === watchlist.itemsLimit) {
      setModal('company-limit-modal');
    } else {
      handleAddCompany();
    }
  }, [
    user.loggedIn,
    hasMultipleWatchlists,
    isWatching,
    watchlist,
    handleLoggedOutAddClick,
    handleRemoveCompany,
    handleAddCompany,
  ]);

  const modals = useMemo(
    () => (
      <>
        {modal === MODAL.GUEST_REGISTRATION && (
          <GuestRegistrationModal
            canClose
            removeWindowScroll
            onClose={() => setModal(MODAL.NONE)}
            onRegisterSuccess={() => {
              trackEvent({
                type: 'track',
                subject: 'addToWatchLists',
                modifier: 'registration',
                action: 'success',
              });
            }}
          />
        )}
        {modal === MODAL.COMPANY_LIMIT && watchlist && (
          <LimitModal
            watchlist={watchlist}
            onClose={() => setModal(MODAL.NONE)}
          />
        )}
        {modal === MODAL.ADD_TO_WATCHLISTS && watchlists && (
          <AddToWatchlistsModal
            data-cy-id="add-remove-to-watchlists-modal"
            companyId={companyId}
            tickerSymbol={tickerSymbol}
            watchlists={watchlists}
            onClose={() => setModal(MODAL.NONE)}
          />
        )}
      </>
    ),
    [companyId, modal, tickerSymbol, trackEvent, watchlist, watchlists]
  );

  /**
   * This is a bit of a hack to avoid layout shift when the user is "watching"
   * this stock after page load.
   */
  const largeLabel = useMemo(
    () => (
      <span className="grid place-items-center [&>*]:[grid-area:1/1]">
        <span
          className={cnLite(
            'flex items-center gap-x0_25',
            isWatching && 'invisible'
          )}
          aria-hidden={isWatching}
        >
          <StarOutlineIcon
            fill="inherit"
            data-cy-id="outline-star"
            className="-mt-px shrink-0 scale-125"
          />
          {t('header.actions.watch')}
        </span>
        <span
          className={cnLite(
            'flex items-center gap-x0_25',
            !isWatching && 'invisible'
          )}
          aria-hidden={!isWatching}
        >
          <StarSolidIcon
            fill="inherit"
            data-cy-id="solid-star"
            className="-mt-px shrink-0 scale-125"
          />
          {t('header.actions.watched')}
        </span>
      </span>
    ),
    [isWatching, t]
  );

  const smallIcon = useMemo(() => {
    return isWatching ? (
      <StarSolidIcon fill="inherit" className="-mt-px shrink-0 scale-125" />
    ) : (
      <StarOutlineIcon fill="inherit" className="-mt-px shrink-0 scale-125" />
    );
  }, [isWatching]);

  const smallLabel = useMemo(() => {
    return isWatching ? t('header.actions.watched') : t('header.actions.watch');
  }, [isWatching, t]);

  const isDisabledForLoggedInUser = user.loggedIn && (!hasFetched || isLoading);

  return {
    key: 'watch',
    cyId: 'add-remove-to-watchlist-toggle',
    isDisabled: !tickerSymbol || !companyId || isDisabledForLoggedInUser,
    largeProps: { label: largeLabel },
    smallProps: { label: smallLabel, icon: smallIcon },
    handleClick,
    modals,
  };
};
